import axios from "axios";
import { DeviceType, REQUEST_STATUS } from "../../../utils/constants";
import { notification } from "antd";
import dayjs from "dayjs";
import DOMPurify from "dompurify";

const HOST_NAME = process.env.REACT_APP_API_HOST_NAME + "/api/";

export const detectDeviceType = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth < 600) {
    return DeviceType.mobile;
  } else if (screenWidth < 992) {
    return DeviceType.tablet;
  } else {
    return DeviceType.pc;
  }
};

export const capitalizeWord = (word) => {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

export function getMobileOperatingSystem() {
  var userAgent = navigator.userAgent || navigator.vendor || window.opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return "Windows Phone";
  }

  if (/android/i.test(userAgent)) {
    return "Android";
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return "iOS";
  }

  return "unknown";
}

export function getIsDesktopBrowser() {
  return window.orientation === undefined;
}

export const constructUrlWithParamsForFetch = (params) => {
  let esc = encodeURIComponent;
  let query = Object.keys(params)
    .map((k) => esc(k) + "=" + esc(params[k]))
    .join("&");
  return query;
};

const createURL = (path) => HOST_NAME + path;

export const sendRequest = async (
  method,
  url,
  params = null,
  data = null,
  isFormData = false
) => {
  url = params
    ? createURL(url) + "?" + constructUrlWithParamsForFetch(params)
    : createURL(url);

  const contentType = isFormData ? false : "application/json";
  const processData = isFormData ? false : true;

  let dataPromise = {};

  let request = axios({
    method: method,
    url: url,
    data: data,
    contentType: contentType,
    processData: processData,
    headers: {
      "X-TenantId": process.env.REACT_APP_API_TENANT_ID,
    },
  });

  return await request
    .then((response) => {
      dataPromise = response?.data;
      if (dataPromise.status === REQUEST_STATUS.error) {
        notification.error({
          message: "An Unexpected Error Occured",
          description: dataPromise.message,
        });
      }

      return dataPromise.data;
    })
    .catch((error) => {
      notification.error({
        message: "An Unexpected Error Occured",
        description: error.message,
      });
      throw error;
    });
};

export const timeAgo = (date) => {
  const now = new Date();
  const secondsPast = Math.floor((now - new Date(date)) / 1000);

  if (secondsPast < 60) {
    return `${secondsPast} seconds ago`;
  } else if (secondsPast < 3600) {
    // less than an hour
    const minutes = Math.floor(secondsPast / 60);
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  } else if (secondsPast < 86400) {
    // less than a day
    const hours = Math.floor(secondsPast / 3600);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  } else if (secondsPast < 2592000) {
    // less than 30 days
    const days = Math.floor(secondsPast / 86400);
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  } else if (secondsPast < 31536000) {
    // less than a year
    const months = Math.floor(secondsPast / 2592000);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  } else {
    const years = Math.floor(secondsPast / 31536000);
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  }
};

export const downloadFiles = (filePath) => {
  if (!filePath || (Array.isArray(filePath) && filePath.length === 0)) {
    console.warn("No file available to download");
    return;
  }

  const files = Array.isArray(filePath) ? filePath : [filePath];

  files.forEach((file) => {
    const link = document.createElement("a");
    link.href = file;
    link.download = file.split("/").pop() || "download"; // Suggests filename
    link.target = "_blank"; // Optional: opens new tab if direct download not supported
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  });
};

export const formatWithTimezone = (data, format = "DD-MM-YYYY HH:mm") => {
  return data ? dayjs.utc(data).local().format(format) : "";
};

export function isValidEmail(email) {
  if (typeof email !== "string") return false;

  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return regex.test(email.trim());
}

export function getFileNameFromPresignedURL(url) {
  const pathname = new URL(url).pathname;
  return (
    decodeURIComponent(pathname.substring(pathname.lastIndexOf("/") + 1)) || ""
  );
}

export const sanitizeHTML = (html) => {
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
  });
};
